import { useEffect, type ComponentType } from 'react';
import { Navigate, useLocation, useMatch } from 'react-router-dom';
import { isAxiosError } from 'axios';

import {
  getRoutePath,
  getRoutePattern,
  ONBOARDING_STEPS,
  PARENT_ROUTES,
} from '@/routes/util';
import { FullPageLoader } from '@/shared/components';
import { useCurrentUser } from '@/shared/auth/hooks/useCurrentUser';
import { useGetWorkspace } from '@/generated/hooks/agora/v1/workspace';
import { setSentryUser } from '@/modules/sentry';

import { useAuth } from '../hooks/use-auth';
import { ErrorScreen } from './ErrorScreen';
import {
  selectCurrentOrganizationId,
  selectSetCurrentOrganizationId,
  selectSetCurrentWorkspaceId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';
import { V1OnboardingState } from '@/generated/hooks/agora/models';
import { checkIsAccessDeniedError } from '@/shared/utils/error-handler';

/**
 * Higher-Order Component that wraps a component with authentication protection.
 *
 * This HOC performs the following checks:
 * 1. Verifies Auth0 authentication status
 * 2. Checks if user exists in backend database via /api/v1/me
 * 3. Redirects to /onboarding if authenticated but user not found in backend (403)
 * 4. Shows error screen with retry for other backend failures
 *
 * Usage:
 * ```tsx
 * import { withProtectedRoute } from '@/shared/auth';
 *
 * const MyComponent = () => <div>Protected Content</div>;
 * const ProtectedMyComponent = withProtectedRoute(MyComponent);
 *
 * // In your router or JSX
 * <ProtectedMyComponent />
 * ```
 */
export const withProtectedRoute = <P extends object>(
  Component: ComponentType<P>,
) => {
  return (props: P) => {
    const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
    const location = useLocation();
    const setCurrentOrganizationId = useUserPreferencesStore(
      selectSetCurrentOrganizationId,
    );
    const currentOrganizationId = useUserPreferencesStore(
      selectCurrentOrganizationId,
    );
    const setCurrentWorkspaceId = useUserPreferencesStore(
      selectSetCurrentWorkspaceId,
    );
    const currentWorkspaceId = useUserPreferencesStore(
      selectCurrentWorkspaceId,
    );

    const onboardingMatch = useMatch(getRoutePattern('ONBOARDING'));
    const isOnboardingPage = !!onboardingMatch;

    const isAcceptInvitationPage = Boolean(
      useMatch(getRoutePattern('INVITATION')),
    );

    // Match workspace routes to extract orgId and workspaceId
    const workspaceRouteMatch = useMatch(`${PARENT_ROUTES.WORKSPACE}/*`);
    const isWorkspaceRoute = Boolean(workspaceRouteMatch);
    const urlOrgId = workspaceRouteMatch?.params.orgId;
    const urlWorkspaceId = workspaceRouteMatch?.params.workspaceId;

    // Check if user is on the access denied page (to prevent infinite redirect loop)
    const isWorkspaceAccessDeniedPage = Boolean(
      useMatch(getRoutePattern('WORKSPACE_ACCESS_DENIED')),
    );

    const {
      isLoading: isUserLoading,
      error: currentUserError,
      data: currentUser,
    } = useCurrentUser({
      query: {
        enabled: isAuthenticated,
        retry: false,
      },
    });

    // Check workspace access when on workspace routes (except access denied page)
    const shouldCheckWorkspaceAccess = Boolean(
      isAuthenticated &&
        currentUser?.data?.user &&
        isWorkspaceRoute &&
        !isWorkspaceAccessDeniedPage &&
        urlWorkspaceId,
    );

    const { isLoading: isWorkspaceLoading, error: workspaceError } =
      useGetWorkspace(urlOrgId ?? '', urlWorkspaceId ?? '', {
        query: {
          enabled: shouldCheckWorkspaceAccess,
          retry: false,
        },
      });

    const isWorkspaceAccessDenied = checkIsAccessDeniedError(workspaceError);

    const lastOrg = currentUser?.data.user?.last_org;
    const lastWorkspace = currentUser?.data.user?.last_workspace;

    // eslint-disable-next-line no-restricted-syntax -- reason: Sync last org/workspace from user data to store
    useEffect(() => {
      if (lastOrg && !currentOrganizationId) {
        setCurrentOrganizationId(lastOrg);
      }
      if (lastWorkspace && !currentWorkspaceId) {
        setCurrentWorkspaceId(lastWorkspace);
      }
    }, [
      currentOrganizationId,
      currentWorkspaceId,
      lastOrg,
      lastWorkspace,
      setCurrentOrganizationId,
      setCurrentWorkspaceId,
    ]);

    // eslint-disable-next-line no-restricted-syntax -- reason: Set Sentry user email once user data is fetched for error tracking
    useEffect(() => {
      const email = currentUser?.data?.user?.email;
      setSentryUser(email);
    }, [currentUser]);

    const isOnboardingCompleted =
      currentUser?.data?.user?.onboarding_state === V1OnboardingState.COMPLETED;

    const workspaceCreationPending =
      currentUser?.data?.user?.onboarding_state ===
      V1OnboardingState.WORKSPACE_CREATION_PENDING;

    // Check if user data is missing (403 forbidden error)
    const isUserDataMissing =
      isAxiosError(currentUserError) &&
      currentUserError.response?.status === 403;

    // Check if backend returned an error (non-403 status)
    const isBackendError = isAxiosError(currentUserError) && !isUserDataMissing;

    // If user data is missing - onboarding is not started yet
    // If onboarding started and not completed - redirect to onboarding workspace step
    // Exception: Allow invitation page to render even if user is not onboarded
    const shouldRedirectToOnboarding =
      !isOnboardingPage &&
      !isAcceptInvitationPage &&
      (isUserDataMissing || !isOnboardingCompleted);

    // If user is on user-intent step which means onboarding is completed,
    // if we don't skip this while user in user-intent step,
    // it'll redirect to chat page again, which will skip user-intent step
    const shouldRedirectToAutomations =
      currentOrganizationId &&
      currentWorkspaceId &&
      isOnboardingCompleted && // Only redirect if onboarding is marked as completed
      ((isOnboardingPage &&
        onboardingMatch.params.onboardingStep !==
          ONBOARDING_STEPS.USER_INTENT) ||
        location.pathname === getRoutePath('HOME'));

    // Should show loading screen if Auth0 is still loading, user data is loading, or workspace access is being checked
    const shouldShowLoadingScreen =
      isAuthLoading ||
      isUserLoading ||
      (shouldCheckWorkspaceAccess && isWorkspaceLoading);

    // If user is not authenticated and Auth0 is not loading, redirect to login
    const shouldRedirectToLogin = !isAuthenticated && !isAuthLoading;

    // Redirect to workspace access denied page if user doesn't have access to the workspace
    const shouldRedirectToWorkspaceAccessDenied =
      isWorkspaceAccessDenied &&
      urlOrgId &&
      urlWorkspaceId &&
      !isWorkspaceAccessDeniedPage;

    if (shouldRedirectToLogin) {
      return (
        <Navigate
          to={getRoutePath('LOGIN')}
          replace
          state={{ from: location.pathname + location.search }}
        />
      );
    }

    if (isBackendError) {
      return <ErrorScreen />;
    }

    if (shouldShowLoadingScreen) {
      return <FullPageLoader />;
    }

    if (shouldRedirectToOnboarding) {
      return (
        <Navigate
          to={getRoutePath('ONBOARDING', {
            onboardingStep: workspaceCreationPending
              ? ONBOARDING_STEPS.WORKSPACE
              : ONBOARDING_STEPS.ORGANIZATION,
          })}
          replace
        />
      );
    }

    if (shouldRedirectToWorkspaceAccessDenied) {
      return (
        <Navigate
          to={getRoutePath('WORKSPACE_ACCESS_DENIED', {
            orgId: urlOrgId,
            workspaceId: urlWorkspaceId,
          })}
          replace
        />
      );
    }

    if (shouldRedirectToAutomations) {
      return (
        <Navigate
          to={getRoutePath('AUTOMATIONS', {
            orgId: currentOrganizationId,
            workspaceId: currentWorkspaceId,
          })}
          replace
        />
      );
    }

    // User is authenticated and verified, render the protected component
    return <Component {...props} />;
  };
};
